import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, ColorSelector, Icon } from '../../../../shared/ui';
import { IconMap } from '../../../../shared/sprite';
import { useCollaboration } from '../../../../entities/collaboration';

interface PageCoverColorPickerProps {
  onColorChange: (color: string) => void;
  currentCoverColor: string | null;
  closeOptions: () => void;
}

const defaultColor = '#0E9384';
const HEX_COLOR_REGEX = /^[0-9A-Fa-f]{6}$/i;

export const PageCoverColorPicker: React.FC<PageCoverColorPickerProps> = ({
  onColorChange,
  currentCoverColor,
  closeOptions,
}) => {
  const initialColor = currentCoverColor || defaultColor;

  const { originalBrandColor, updateCoverColor, updateCoverImageUrl } =
    useCollaboration();

  const [color, setColor] = useState(initialColor);
  const [inputColor, setInputColor] = useState(initialColor.replace('#', ''));
  const [isEditOpen, setIsEditOpen] = useState(!currentCoverColor);
  const [isShowColorPicker, setIsShowColorPicker] = useState(false);

  const handleColorPickerChange = (newColor: string) => {
    setColor(newColor);
    setInputColor(newColor.replace('#', ''));
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.substring(0, 6);
    setInputColor(newValue);

    if (HEX_COLOR_REGEX.test(newValue)) {
      setColor(`#${newValue}`);
    }
  };

  const handleApply = () => {
    if (HEX_COLOR_REGEX.test(inputColor)) {
      const newColor = `#${inputColor}`;
      onColorChange(newColor);
      setIsEditOpen(false);
      setIsShowColorPicker(false);
      closeOptions();
    }
  };

  const handleCancel = () => {
    if (currentCoverColor) {
      setColor(currentCoverColor);
      setInputColor(currentCoverColor.replace('#', ''));
    }
    setIsEditOpen(false);
    setIsShowColorPicker(false);
  };

  const handleCloseColorPicker = useCallback(() => {
    setIsShowColorPicker(false);
  }, []);

  const handleToggleColorPicker = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsShowColorPicker((prev) => !prev);
  }, []);

  const handleRestoreCompanyColor = () => {
    if (!originalBrandColor) {
      return;
    }
    updateCoverColor(originalBrandColor);
    updateCoverImageUrl(null);
    closeOptions();
  };

  useEffect(() => {
    if (!currentCoverColor) {
      setIsEditOpen(true);
      return;
    }

    setColor(initialColor);
  }, [currentCoverColor, initialColor]);

  useEffect(() => {
    if (color) {
      setInputColor(color.replace('#', ''));
    }
  }, [color]);

  const isValid = useMemo(() => {
    return HEX_COLOR_REGEX.test(inputColor);
  }, [inputColor]);

  return (
    <div className="w-full">
      <div>
        <div className="mb-1.5  flex justify-between">
          <span className="text-sm font-medium text-gray-700">
            Select color or enter HEX-code
          </span>
          {originalBrandColor && (
            <Button
              className="!h-5 !p-0"
              color="link"
              variant="text"
              size="sm"
              onClick={handleRestoreCompanyColor}
            >
              Use company brand color
            </Button>
          )}
        </div>

        {isEditOpen ? (
          <div className="">
            <div className="flex items-center mb-4">
              <div
                className="flex-shrink-0 w-5 h-5 mr-2 border rounded-md cursor-pointer border-base-black-8"
                style={{
                  backgroundColor: isValid ? `#${inputColor}` : color,
                }}
                onClick={handleToggleColorPicker}
              />

              <div className="flex items-center flex-1 px-2 bg-white border border-gray-300 rounded-md shadow-xs focus-within:border-primary-600">
                <span className="mr-1 text-gray-500">#</span>
                <input
                  type="text"
                  className="flex-1 px-1 py-2 uppercase outline-none"
                  autoFocus
                  value={inputColor}
                  onChange={handleCustomColorChange}
                  maxLength={6}
                />

                <button
                  className="p-1 text-gray-400 hover:text-gray-600"
                  onClick={handleToggleColorPicker}
                >
                  <Icon glyph={IconMap.ChevronDown} width={16} />
                </button>
              </div>
            </div>

            {isShowColorPicker && (
              <div className="relative">
                <div className="absolute z-10">
                  <ColorSelector
                    value={color}
                    onChange={handleColorPickerChange}
                    onClose={handleCloseColorPicker}
                    isUseClickOutside={true}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end mt-3 space-x-2">
              {currentCoverColor && (
                <Button
                  className="px-2 w-19"
                  onClick={handleCancel}
                  color="secondary"
                  size="sm"
                  variant="text"
                >
                  Cancel
                </Button>
              )}

              <Button
                className="px-2 w-19"
                onClick={handleApply}
                disabled={!isValid}
                color="primary"
                variant="text"
                size="sm"
              >
                Apply
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md shadow-xs"
            onClick={() => {
              setIsEditOpen(true);
              setIsShowColorPicker(true);
            }}
            role="button"
          >
            <div
              className="w-5 h-5 mr-2 border rounded-md border-base-black-8"
              style={{ backgroundColor: color }}
            />

            <div className="flex-1 text-gray-600">{color}</div>

            <span className="font-medium text-primary-700">Edit</span>
          </div>
        )}
      </div>
    </div>
  );
};
